import {
  questionImportCandidateListSchema,
  type QuestionImportCandidate,
  type SourceConnectorConfigInput,
  type SourceDocumentInput
} from "../../import-contracts/src/index";

type SourceConnectorInput = SourceConnectorConfigInput;

export interface ApiClientOptions {
  baseUrl: string;
  /** Injectable fetch (defaults to global fetch). Access JWT cookies ride along automatically. */
  fetchImpl?: typeof fetch;
}

export interface ApiError {
  ok: false;
  error: string;
  code?: string;
  [k: string]: unknown;
}

export interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string;
  metadata?: unknown;
  createdAt: string;
}

export class ApiRequestError extends Error {
  constructor(public status: number, public payload: ApiError) {
    super(`${payload.error}${payload.code ? `: ${payload.code}` : ""}`);
    this.name = "ApiRequestError";
  }
}

export function createApiClient(opts: ApiClientOptions) {
  const f = opts.fetchImpl ?? fetch;
  const base = opts.baseUrl.replace(/\/$/, "");

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    // Same-origin requests (Pages + Worker on one domain) carry the Cloudflare Access
    // cookie automatically. For a cross-origin API host, set credentials: "include" in
    // a browser-only wrapper and enable CORS on the worker.
    const res = await f(`${base}${path}`, {
      ...init,
      headers: { ...(init?.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}), ...init?.headers }
    });
    const data = (await res.json().catch(() => ({}))) as unknown;
    if (!res.ok) {
      throw new ApiRequestError(res.status, data as ApiError);
    }
    return data as T;
  }

  return {
    health: () => request<{ ok: boolean; service: string }>("/health"),

    createSourceDocument: (input: SourceDocumentInput) =>
      request<{ ok: true; sourceDocument: { id: string } }>("/source-documents", {
        method: "POST",
        body: JSON.stringify(input)
      }),

    uploadFile: (sourceDocumentId: string, file: File | Blob) => {
      const form = new FormData();
      form.set("file", file);
      return request<{ ok: true; checksum: string; r2Key: string }>(
        `/source-documents/${sourceDocumentId}/upload`,
        { method: "POST", body: form }
      );
    },

    createImportJob: (sourceDocumentId: string) =>
      request<{ ok: true; importJob: { id: string; status: string } }>("/import-jobs", {
        method: "POST",
        body: JSON.stringify({ sourceDocumentId })
      }),

    getImportJob: (id: string) =>
      request<{ ok: true; importJob: { id: string; status: string }; candidateCount: number }>(
        `/import-jobs/${id}`
      ),

    listCandidates: async (importJobId?: string): Promise<QuestionImportCandidate[]> => {
      const qs = importJobId ? `?importJobId=${encodeURIComponent(importJobId)}` : "";
      const data = await request<{ ok: true; candidates: unknown }>(`/review-candidates${qs}`);
      return questionImportCandidateListSchema.parse(data.candidates);
    },

    approveCandidate: (id: string) =>
      request<{ ok: true; status: string }>(`/review-candidates/${id}/approve`, { method: "POST" }),

    rejectCandidate: (id: string) =>
      request<{ ok: true; status: string }>(`/review-candidates/${id}/reject`, { method: "POST" }),

    publishCandidate: (id: string) =>
      request<{ ok: true; published: boolean }>(`/publish/${id}`, { method: "POST" }),

    listAudit: (limit = 100) =>
      request<{ ok: true; entries: AuditEntry[] }>(`/audit-log?limit=${limit}`).then((r) => r.entries),

    discover: (connector: SourceConnectorInput, seedPaths?: string[]) =>
      request<{ ok: true; discovered: number; sourceDocuments: { id: string; sourceUrl: string | null; pdfKind: string | null }[] }>(
        "/discover",
        { method: "POST", body: JSON.stringify({ connector, seedPaths }) }
      )
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
